import { useState, useEffect, useCallback, useRef } from 'react'
import { sightingAPI } from '../services/api'
import { createLogger } from '../utils/logger'

const logger = createLogger('useLiveFeed')
const REFRESH_INTERVAL_MS = 60_000
const PAGE_SIZE = 30

/**
 * useLiveFeed - Global sighting feed with offset-based pagination and auto-refresh.
 * Page 0 auto-refreshes every 60s (resets to top). loadMore() appends subsequent pages.
 * @param {Object} [userLocation] - Optional { lat, lng } for radius filtering
 * @returns {{ items, loading, loadingMore, error, hasMore, refresh, loadMore }}
 */
export function useLiveFeed(userLocation = null) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [hasMore, setHasMore] = useState(true)
  const offsetRef = useRef(0)
  const intervalRef = useRef(null)
  const mountedRef = useRef(true)

  const doFetch = useCallback(async (offset) => {
    const isFirstPage = offset === 0
    if (isFirstPage) setLoading(true)
    else setLoadingMore(true)

    try {
      const params = { limit: PAGE_SIZE, offset }
      if (userLocation?.lat && userLocation?.lng) {
        params.lat = userLocation.lat
        params.lng = userLocation.lng
        params.radius = 25
      }
      const response = await sightingAPI.getLiveFeed(params)
      const fetched = response.data?.items || []
      if (mountedRef.current) {
        if (isFirstPage) {
          setItems(fetched)
          offsetRef.current = fetched.length
        } else {
          setItems(prev => [...prev, ...fetched])
          offsetRef.current = offset + fetched.length
        }
        setHasMore(fetched.length === PAGE_SIZE)
        setError(null)
      }
    } catch (err) {
      logger.error('Failed to fetch live feed', err)
      if (mountedRef.current) setError(err)
    } finally {
      if (mountedRef.current) {
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }, [userLocation?.lat, userLocation?.lng])

  useEffect(() => {
    mountedRef.current = true
    doFetch(0)
    intervalRef.current = setInterval(() => doFetch(0), REFRESH_INTERVAL_MS)
    return () => {
      mountedRef.current = false
      clearInterval(intervalRef.current)
    }
  }, [doFetch])

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      // Pause auto-refresh once user pages past the first page
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      doFetch(offsetRef.current)
    }
  }, [doFetch, loadingMore, hasMore])

  const refresh = useCallback(() => {
    // Resume auto-refresh when returning to page 0
    offsetRef.current = 0
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => doFetch(0), REFRESH_INTERVAL_MS)
    }
    doFetch(0)
  }, [doFetch])

  return { items, loading, loadingMore, error, hasMore, refresh, loadMore }
}
